import { useEffect, useState, useRef } from "react";
import { Box, styled, Button, Checkbox, FormControlLabel, Grid, Icon } from "@mui/material";
import { LoadingButton } from '@mui/lab';
import { Breadcrumb, SimpleCard } from "app/components";
import { Span } from "app/components/Typography";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { Editor } from '@tinymce/tinymce-react';

import { useDispatch, useSelector } from 'react-redux';
import { GetStories, CreateStory, UpdateStory } from "../../../store/actions/storyAction"
import { STORY_SUCCESS_MESSAGE_CLEAR, STORY_ERROR_CLEAR } from "../../../store/types/storyTypes"

import Swal from 'sweetalert2'
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useNavigate, useParams, useLocation } from "react-router-dom";

const TextField = styled(TextValidator)(() => ({
    width: "100%",
    marginBottom: "16px",
}));

const Container = styled("div")(({ theme }) => ({
    margin: "30px",
    [theme.breakpoints.down("sm")]: { margin: "16px" },
    "& .breadcrumb": {
        marginBottom: "30px",
        [theme.breakpoints.down("sm")]: { marginBottom: "16px" },
    },
}));

const StoryForm = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const location = useLocation();

    const currentPathname = location.pathname;
    const currentPageName = currentPathname.includes('new-story') ? 'New Story' :
        currentPathname.includes('edit-story') ? 'Edit Story' :
            '';

    let { storyid } = useParams();

    const { stories, successMessage, errorMessage } = useSelector((state) => state.Stories);

    // TinyMCE editor ref
    const tmEditorRef = useRef(null);

    const [story, setStory] = useState({
        title: "",
        description: "",
        status: "Draft",
        date: new Date().toISOString().split('T')[0],
        pinned: false,
        isMain: false,
        file: null
    });

    const [callbackResponse, setCallbackResponse] = useState({ type: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [fileError, setFileError] = useState("");

    useEffect(() => {
        dispatch(GetStories()).then(() => {
            dispatch({ type: STORY_SUCCESS_MESSAGE_CLEAR })
        })
    }, []);

    useEffect(() => {
        if (storyid && storyid.length > 0 && stories) {
            const currentStory = stories.find(item => item._id === storyid);
            if (currentStory) {
                setStory(currentStory);
                if (currentStory.image) {
                    setImagePreview(currentStory.image);
                }
                // Set the editor content after story is loaded
                if (tmEditorRef.current && currentStory.description) {
                    tmEditorRef.current.setContent(currentStory.description);
                }
            } else {
                setCallbackResponse({ type: "error", message: "Invalid story data." });
            }
        }
    }, [stories, storyid])

    const handleChange = ({ target: { name, value, type, checked } }) => {
        if (type === "checkbox") {
            setStory(prev => ({ ...prev, [name]: checked }));
        } else {
            setStory(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = ({ target: { files } }) => {
        if (files && files[0]) {
            const file = files[0];

            // Validate file type (image or video)
            const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
            const allValidTypes = [...validImageTypes, ...validVideoTypes];

            if (!allValidTypes.includes(file.type)) {
                setFileError("Please upload a valid image (JPEG, PNG, GIF, WebP) or video (MP4, WebM, OGG, MOV) file");
                setStory(prev => ({ ...prev, file: null }));
                setImagePreview(null);
                return;
            }

            // Validate file size (max 50MB)
            const maxSize = 50 * 1024 * 1024; // 50MB
            if (file.size > maxSize) {
                setFileError("File size must be less than 50MB");
                setStory(prev => ({ ...prev, file: null }));
                setImagePreview(null);
                return;
            }

            setFileError("");
            setStory(prev => ({ ...prev, file: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);

        // Check if file is provided for new stories
        if (!storyid) {
            if (!story.file || !(story.file instanceof File)) {
                setCallbackResponse({ type: "error", message: "Media file (image or video) is required" });
                setLoading(false);
                return;
            }
        }

        try {
            if (storyid && storyid.length > 0) {
                const updateData = {
                    _id: storyid,
                    title: story.title,
                    description: tmEditorRef.current?.getContent() || story.description,
                    status: story.status,
                    date: story.date,
                    pinned: story.pinned,
                    isMain: story.isMain
                };

                // Only add file if it's a new File object being uploaded
                if (story.file && story.file instanceof File) {
                    updateData.file = story.file;
                }

                await dispatch(UpdateStory(updateData));
            } else {
                // For new stories, file is required
                const createData = {
                    title: story.title,
                    description: tmEditorRef.current?.getContent() || story.description,
                    status: story.status,
                    date: story.date,
                    pinned: story.pinned,
                    isMain: story.isMain,
                    file: story.file  // File is required here
                };

                await dispatch(CreateStory(createData));
            }
        } catch (error) {
            setCallbackResponse({ type: "error", message: "An error occurred" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (successMessage.create || successMessage.update) {
            const msg = successMessage.create || successMessage.update;
            setCallbackResponse({ type: "success", message: msg });
            setTimeout(() => {
                navigate("/posts/story");
            }, 2000);
        }

        if (errorMessage) {
            setCallbackResponse({ type: "error", message: errorMessage });
            dispatch({ type: STORY_ERROR_CLEAR })
        }
    }, [successMessage, errorMessage])

    const handleClose = () => {
        navigate("/posts/story");
    };

    return (
        <Container>
            <Box className="breadcrumb">
                <Breadcrumb routeSegments={[{ name: "Dashboard", path: "/" }, { name: "Stories", path: "/posts/story" }, { name: currentPageName }]} />
            </Box>

            <SimpleCard title={currentPageName}>
                <ValidatorForm onSubmit={handleSubmit} onError={() => null}>
                    <Grid container spacing={2}>
                        <Grid item sm={6} xs={12}>
                            <TextField
                                type="text"
                                name="title"
                                id="title"
                                label="Title"
                                value={story.title}
                                onChange={handleChange}
                                validators={["required"]}
                                errorMessages={["Title is required"]}
                            />
                        </Grid>

                        <Grid item sm={6} xs={12}>
                            <TextField
                                type="date"
                                name="date"
                                id="date"
                                label="Date"
                                value={story.date}
                                onChange={handleChange}
                                InputLabelProps={{ shrink: true }}
                                validators={["required"]}
                                errorMessages={["Date is required"]}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <label className="form-label">Description</label>
                            <Box sx={{ mb: 2, width: '100%' }}>
                                <Editor
                                    apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                    onInit={(evt, editor) => tmEditorRef.current = editor}
                                    initialValue={story.description}
                                    init={{
                                        height: 300,
                                        menubar: false,
                                        plugins: [
                                            'advlist autolink lists link image charmap print preview anchor',
                                            'searchreplace visualblocks code fullscreen',
                                            'insertdatetime media table paste code help wordcount'
                                        ],
                                        toolbar: 'undo redo | formatselect | ' +
                                            'bold italic backcolor | alignleft aligncenter ' +
                                            'alignright alignjustify | bullist numlist outdent indent | ' +
                                            'removeformat | help',
                                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                                    }}
                                />
                            </Box>
                        </Grid>

                        <Grid item sm={6} xs={12}>
                            <TextField
                                select
                                name="status"
                                id="status"
                                label="Status"
                                value={story.status}
                                onChange={handleChange}
                                validators={["required"]}
                                errorMessages={["Status is required"]}
                                SelectProps={{
                                    native: true
                                }}
                            >
                                <option value="Draft">Draft</option>
                                <option value="Publish">Publish</option>
                            </TextField>
                        </Grid>

                        <Grid item sm={6} xs={12}>
                            <TextField
                                type="file"
                                name="file"
                                id="file"
                                label="Story Media (Image or Video) *"
                                onChange={handleFileChange}
                                inputProps={{
                                    accept: "image/*,video/*"
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            {fileError && (
                                <Box sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 1 }}>
                                    {fileError}
                                </Box>
                            )}
                            {!storyid && !story.file && (
                                <Box sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 1 }}>
                                    Media file is required for new stories
                                </Box>
                            )}
                        </Grid>

                        {imagePreview && (
                            <Grid item xs={12}>
                                {story.file && story.file.type.startsWith('video/') ? (
                                    <video
                                        width="300"
                                        height="300"
                                        controls
                                        style={{
                                            maxWidth: "300px",
                                            maxHeight: "300px",
                                            borderRadius: "8px",
                                            marginTop: "10px"
                                        }}
                                    >
                                        <source src={imagePreview} type={story.file.type} />
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <Box
                                        component="img"
                                        src={imagePreview}
                                        alt="Preview"
                                        sx={{
                                            maxWidth: "300px",
                                            maxHeight: "300px",
                                            borderRadius: "8px",
                                            marginTop: "10px"
                                        }}
                                    />
                                )}
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="pinned"
                                        checked={story.pinned || false}
                                        onChange={handleChange}
                                    />
                                }
                                label="Pinned"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isMain"
                                        checked={story.isMain || false}
                                        onChange={handleChange}
                                    />
                                }
                                label="Set as Main Story"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Snackbar
                                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                open={!!callbackResponse.message}
                                autoHideDuration={6000}
                                onClose={() => setCallbackResponse({ type: "", message: "" })}
                            >
                                <Alert
                                    severity={callbackResponse.type}
                                    variant="filled"
                                    sx={{ width: '300px' }}
                                >
                                    {callbackResponse.message}
                                </Alert>
                            </Snackbar>

                            <LoadingButton
                                loading={loading}
                                variant="contained"
                                color="primary"
                                type="submit"
                                sx={{ mr: 1 }}
                            >
                                {storyid ? "Update Story" : "Create Story"}
                            </LoadingButton>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    </Grid>
                </ValidatorForm>
            </SimpleCard>
        </Container>
    );
};

export default StoryForm;

